// COPIED from ptc-core web/src/shell/FeedbackDialog.tsx @ de39fff (fleet feedback widget contract
// v1, 2026-10-21). Changes from the source: `app: 'tax-lifecycle'` (was 'crm', via
// FEEDBACK_APP_ID); field-validation display uses this app's own FeedbackValidationError.firstFor
// (services/feedbackApi.ts) rather than ptc-core's shared ApiValidationError; Fluent
// Field/Textarea styling via makeStyles rather than Tailwind utility classes (this app has no
// Tailwind wired into its build -- see tailwind.config absence; it is an unused devDependency).
// Conformance: FeedbackWidgetConformance.test.tsx.
//
// What is sent is SHOWN before it is sent (the "attach summary" details below). The SSN/EIN
// warning sits above the text box so the server-side guard rarely has to fire; when it does, its
// message is shown on the field, the typed text is kept, and no reference appears (contract §5,
// case C8).

import { useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Textarea,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { Trans, useTranslation } from 'react-i18next';
import {
  FEEDBACK_APP_ID,
  FeedbackValidationError,
  useCreateFeedback,
  type FeedbackKind,
} from '../../services/feedbackApi';
import { buildFeedbackContext, type FeedbackContext } from '../../feedback/feedbackContext';

interface FeedbackDialogProps {
  kind: FeedbackKind | null;
  onClose: () => void;
  /** Test seam; production captures the live page. */
  captureContext?: () => FeedbackContext;
}

const COPY: Record<FeedbackKind, { title: string; label: string }> = {
  problem: { title: 'shell.feedback.problemTitle', label: 'shell.feedback.problemLabel' },
  improvement: { title: 'shell.feedback.improvementTitle', label: 'shell.feedback.improvementLabel' },
};

const useStyles = makeStyles({
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  warning: {
    color: tokens.colorPaletteDarkOrangeForeground1,
    fontSize: tokens.fontSizeBase200,
  },
  attach: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  attachGrid: {
    marginTop: tokens.spacingVerticalXS,
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    columnGap: tokens.spacingHorizontalS,
    rowGap: tokens.spacingVerticalXXS,
  },
  error: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase200,
  },
});

export function FeedbackDialog({ kind, onClose, captureContext = buildFeedbackContext }: FeedbackDialogProps) {
  if (kind === null) return null;
  // Keyed on kind so switching between the two entries starts a fresh form.
  return <FeedbackForm key={kind} kind={kind} onClose={onClose} captureContext={captureContext} />;
}

function FeedbackForm({ kind, onClose, captureContext }: Required<FeedbackDialogProps> & { kind: FeedbackKind }) {
  const { t } = useTranslation();
  const styles = useStyles();
  // Captured when the dialog OPENS: the route the reporter was on, not wherever they click Send.
  const context = useMemo(() => captureContext(), [captureContext]);
  const [text, setText] = useState('');
  const [expected, setExpected] = useState('');
  const [creditByName, setCreditByName] = useState(true); // defaults ON, per contract §2
  const create = useCreateFeedback();

  const fieldErrors = create.error instanceof FeedbackValidationError ? create.error : null;
  const otherError = create.error && !fieldErrors;
  const reference = create.data?.reference;

  function send() {
    create.mutate({
      kind,
      text: text.trim(),
      expected: kind === 'problem' && expected.trim() ? expected.trim() : undefined,
      creditByName,
      app: FEEDBACK_APP_ID,
      context,
    });
  }

  return (
    <Dialog open modalType="modal" onOpenChange={(_, data) => { if (!data.open) onClose(); }}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{t(COPY[kind].title)}</DialogTitle>
          <DialogContent className={styles.content}>
            {reference ? (
              <p role="status">
                <Trans i18nKey="shell.feedback.thanks" values={{ reference }} components={{ strong: <strong /> }} />
              </p>
            ) : (
              <>
                <p className={styles.warning} role="note">{t('shell.feedback.sensitiveWarning')}</p>
                <Field
                  label={t(COPY[kind].label)}
                  required
                  validationMessage={fieldErrors?.firstFor('text')}
                >
                  <Textarea value={text} onChange={(_, d) => setText(d.value)} rows={5} maxLength={4000} />
                </Field>
                {kind === 'problem' && (
                  <Field label={t('shell.feedback.expected')} validationMessage={fieldErrors?.firstFor('expected')}>
                    <Textarea value={expected} onChange={(_, d) => setExpected(d.value)} rows={3} maxLength={2000} />
                  </Field>
                )}
                <Checkbox
                  checked={creditByName}
                  onChange={(_, d) => setCreditByName(d.checked === true)}
                  label={t('shell.feedback.credit')}
                />
                <details className={styles.attach}>
                  <summary>{t('shell.feedback.attachSummary')}</summary>
                  <dl className={styles.attachGrid}>
                    <dt>{t('shell.feedback.contextPage')}</dt><dd>{context.route}</dd>
                    <dt>{t('shell.feedback.contextVersion')}</dt><dd>{context.buildSha}</dd>
                    <dt>{t('shell.feedback.contextScreen')}</dt><dd>{context.viewport}</dd>
                    <dt>{t('shell.feedback.contextTime')}</dt><dd>{context.clientTime} ({context.tz})</dd>
                    <dt>{t('shell.feedback.contextBrowser')}</dt><dd>{context.userAgent}</dd>
                    <dt>{t('shell.feedback.contextErrors')}</dt><dd>{context.recentErrors.length}</dd>
                    <dt>{t('shell.feedback.contextLanguage')}</dt><dd>{context.uiLanguage}</dd>
                  </dl>
                </details>
                {otherError && (
                  <p role="alert" className={styles.error}>{t('shell.feedback.sendFailed')}</p>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions>
            {reference ? (
              <Button appearance="primary" onClick={onClose}>{t('common.close')}</Button>
            ) : (
              <>
                <Button appearance="secondary" onClick={onClose}>{t('common.cancel')}</Button>
                <Button appearance="primary" disabled={!text.trim() || create.isPending} onClick={send}>
                  {create.isPending ? t('shell.feedback.sending') : t('shell.feedback.send')}
                </Button>
              </>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
